import React, {useCallback, useContext, useMemo} from 'react';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {ValidationContext} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';

import {FlatButton} from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';

import {DEFAULT_IP_RANGE} from '../constants';

import {IpRangeView} from './ip-range-view';
import {IpRangesSkeleton} from './ip-ranges-skeleton';

import type {AccessType, IpRangeKey} from '../typedef';
import type {ValidationContextType} from 'src/contexts/validation-context';
import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {Error} from 'src/openapi/admin/models/Error';
import type {IpRange} from 'src/openapi/admin/models/IpRange';


type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    ipRanges: Array<IpRange>,
    onChange: React.Dispatch<React.SetStateAction<Array<IpRange>>>,
    setLastEditedRange: (id: number) => void,
    disabled?: boolean,
};

const SECTION_NAME = 'IP Ranges';

export const IpRangesSection: React.FC<Props> = props => {
    const {isLoading, loadingError, ipRanges, onChange, setLastEditedRange, disabled} = props;
    const {removeError, getErrorsByControlId} = useContext<ValidationContextType>(ValidationContext);

    const onAddIPRange = useCallback(() => {
        onChange(prevRanges => [...prevRanges, DEFAULT_IP_RANGE]);
    }, [onChange]);

    const onRemoveIPRange = useCallback((id: number) => {
        onChange(prevRanges => prevRanges.filter((range, i) => i !== id));
        removeError(`account-ip-range-${id}`);
    }, [onChange, removeError]);

    const onChangeAccess = useCallback((id: number, key: AccessType, checked: boolean) => {
        onChange(prevRanges => prevRanges.map((range, i) => ({
            ...range,
            [key]: i === id ? checked : range[key],
        })));
    }, [onChange]);

    const onChangeIPRange = useCallback((id: number, value: string, key: IpRangeKey) => {
        onChange(prevState => prevState.map((range, i) => (
            i === id ? {...range, [key]: value || ''} : range
        )));
        setLastEditedRange(id);
    }, [setLastEditedRange, onChange]);

    const disableAddIPRange = useMemo(
        () => disabled || ipRanges.some((range, i) => {
            const hasError = !!getErrorsByControlId(`account-ip-range-${i}`).length;
            return hasError || !range.start || !range.end;
        }),
        [ipRanges, getErrorsByControlId, disabled],
    );

    return (
        <Skeleton view={<IpRangesSkeleton/>} shown={isLoading}>
            <ServerResponseHandler
                smallView={true}
                sectionName={SECTION_NAME}
                serverError={loadingError}
                accessRight={ACCESS_RIGHTS.ACCOUNT_SCREEN_VIEW as AccessRight}>
                <div style={{width: 630}}>
                    <Group direction="v" gap="xs" alignItems="start">
                        <Group gap="xl">
                            <Item style={{width: 330}}>
                                <Label>{SECTION_NAME}</Label>
                            </Item>
                            <Item style={{width: 330}}>
                                <Label>Access</Label>
                            </Item>
                        </Group>
                        <Group alignItems="start" direction="v" gap="s">
                            {ipRanges.map((range, i) => (
                                <IpRangeView
                                    key={i}
                                    {...{
                                        id: i,
                                        range,
                                        totalCount: ipRanges.length,
                                        onChangeRange: onChangeIPRange,
                                        onChangeAccess,
                                        onRemove: onRemoveIPRange,
                                        disabled,
                                    }}
                                />
                            ))}
                            <FlatButton
                                disabled={disableAddIPRange}
                                startIcon={<Icon type={Icons.PLUS_CIRCLE}/>}
                                onClick={onAddIPRange}>
                                Add IP Range
                            </FlatButton>
                        </Group>
                    </Group>
                </div>
            </ServerResponseHandler>
        </Skeleton>
    );
};
